import React, { useState, useMemo } from 'react'
import Plot from 'react-plotly.js'
import { Container, Selector } from '../../../components'
import {
    BaseWorkorder,
    ezScheduleDetails,
    IdentifiableNamedObject,
    Schedule,
} from '../../../models'
import {
    getChangeOrderPrice,
    getTotalPriceExpense,
} from '../../../models/WorkOrder/services'
import { toMMDDYYYY } from '../../../helpers'
import { Theme } from '@material-ui/core'
import { typographyStyle } from '../../../theme/typographyStyle'

const generateDataForPlot = (
    workorders: BaseWorkorder[],
    today: Date,
    ezSchedule: ezScheduleDetails,
) => {
    const amountMap: { [dateStr: string]: number } = {}
    const invoicedMap: { [dateStr: string]: number } = {}
    const workorderDateMap: { [dateStr: string]: number } = {}
    let totalChangeOrderCost = 0

    workorders.forEach((wo) => {
        const date = wo.end_date ? new Date(wo.end_date) : today
        const dateStr = toMMDDYYYY(date)

        amountMap[dateStr] =
            (amountMap[dateStr] || 0) + getTotalPriceExpense(wo)

        if (wo.invoiced_price !== undefined && wo.invoiced_price !== null) {
            invoicedMap[dateStr] =
                (invoicedMap[dateStr] || 0) + wo.invoiced_price
        }

        if (date >= today) {
            workorderDateMap[dateStr] = (workorderDateMap[dateStr] || 0) + 1
        }

        totalChangeOrderCost += getChangeOrderPrice(wo)
    })

    const sortedDates = Object.keys(amountMap).sort(
        (a, b) => new Date(a).getTime() - new Date(b).getTime(),
    )

    const forecastedX: string[] = []
    const forecastedY: number[] = []
    const invoicedX: string[] = []
    const invoicedY: number[] = []
    let cumulativeAmount = 0
    let cumulativeInvoiced = 0

    sortedDates.forEach((dateStr) => {
        cumulativeAmount += amountMap[dateStr]
        forecastedX.push(dateStr)
        forecastedY.push(cumulativeAmount)

        if (invoicedMap[dateStr]) {
            cumulativeInvoiced += invoicedMap[dateStr]
            invoicedX.push(dateStr)
            invoicedY.push(cumulativeInvoiced)
        }
    })

    return {
        forecastedX,
        forecastedY,
        invoicedX,
        invoicedY,
        workorderDateMap,
        totalChangeOrderCost,
    }
}

interface Props {
    theme: Theme
    workorders: BaseWorkorder[]
    scheduleDetail: Schedule | undefined
    ezSchedule: ezScheduleDetails
}

export const BudgetPlot = ({
    theme,
    ezSchedule,
    workorders,
    scheduleDetail,
}: Props) => {
    const [selectedServiceId, setSelectedServiceId] = useState<number>(0)
    const [selectedVendorId, setSelectedVendorId] = useState<number>(0)

    // Create vendor list from workorders
    const workorderVendors = useMemo(() => {
        const vendors: { [vendorId: number]: { id: number; name: string } } = {}

        workorders.forEach((wo) => {
            if (wo.vendor_id && wo.vendor_name) {
                if (!vendors[wo.vendor_id]) {
                    vendors[wo.vendor_id] = {
                        id: wo.vendor_id,
                        name: wo.vendor_name,
                    }
                }
            }
        })

        return Object.values(vendors)
    }, [workorders])

    const filteredWorkorders = useMemo(() => {
        if (selectedServiceId) {
            return workorders.filter(
                (wo) => wo.service_id === selectedServiceId,
            )
        } else if (selectedVendorId) {
            return workorders.filter((wo) => wo.vendor_id === selectedVendorId)
        }
        return workorders
    }, [workorders, selectedServiceId, selectedVendorId])

    // use first phase in array as the selected phase
    const templateServices = ezSchedule.phases[0]?.template_services ?? []

    const totalServiceBudget = useMemo(
        () =>
            templateServices.reduce(
                (total, service) => total + Number(service.budget),
                0,
            ) || 0,
        [templateServices],
    )

    const totalVendorBudget = useMemo(
        () =>
            ezSchedule.template_vendors?.reduce(
                (total, vendor) => total + Number(vendor.budget),
                0,
            ) || 0,
        [ezSchedule.template_vendors],
    )

    const templateServiceList = useMemo(
        () => [
            { id: 0, name: 'All Services', budget: totalServiceBudget },
            ...(templateServices.map((ts) => ({
                id: ts.service.id,
                name: ts.service.name,
            })) || []),
        ],
        [templateServices, totalServiceBudget],
    )

    const vendorList = useMemo(
        () => [
            { id: 0, name: 'All Vendors', budget: totalVendorBudget },
            ...workorderVendors,
        ],
        [workorderVendors, totalVendorBudget],
    )

    const selectedBudget = useMemo(() => {
        if (selectedServiceId === 0 && selectedVendorId === 0) {
            return totalServiceBudget
        } else if (selectedServiceId > 0) {
            return (
                templateServices.find(
                    (service) => service.service.id === selectedServiceId,
                )?.budget || 0
            )
        } else if (selectedVendorId > 0) {
            return (
                ezSchedule.template_vendors.find(
                    (vendor) => vendor.vendor.id === selectedVendorId,
                )?.budget || 0
            )
        }
        return 0
    }, [
        selectedServiceId,
        selectedVendorId,
        totalServiceBudget,
        templateServices,
        ezSchedule.template_vendors,
    ])

    const today = new Date()
    today.setHours(23, 59, 59, 999)

    const {
        forecastedX,
        forecastedY,
        invoicedX,
        invoicedY,
        workorderDateMap,
        totalChangeOrderCost,
    } = useMemo(
        () => generateDataForPlot(filteredWorkorders, today, ezSchedule),
        [filteredWorkorders, today, ezSchedule],
    )

    const potentialWorkordersCount = filteredWorkorders.filter(
        (wo) => wo.end_date && new Date(wo.end_date) <= today,
    ).length

    // Average approved CO $ per past workorder (rate × avg CO cost)
    const weightedCostChangeOrder =
        potentialWorkordersCount > 0
            ? totalChangeOrderCost / potentialWorkordersCount
            : 0

    const { predictionX, predictionY } = useMemo(() => {
        let cumulativePrediction = 0
        const x: string[] = []
        const y: number[] = []

        forecastedX.forEach((date, index) => {
            if (new Date(date) > today) {
                const remainingWorkorders = workorderDateMap[date] || 0
                const predictedAmount =
                    weightedCostChangeOrder * remainingWorkorders
                cumulativePrediction += predictedAmount
                x.push(date)
                y.push(forecastedY[index] + cumulativePrediction)
            }
        })

        return { predictionX: x, predictionY: y }
    }, [
        forecastedX,
        forecastedY,
        today,
        workorderDateMap,
        weightedCostChangeOrder,
    ])

    const numXTicks = Math.floor(Math.max(10, forecastedX.length / 4))

    return (
        <Container
            style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
        >
            <Container
                style={{
                    marginTop: theme.spacing(3),
                    marginRight: theme.spacing(9),
                    marginLeft: theme.spacing(5),
                    maxWidth: 250,
                    display: 'flex',
                    gap: theme.spacing(2),
                }}
            >
                <Selector
                    label="Service Filter"
                    currentValue={selectedServiceId}
                    data={templateServiceList}
                    getDisplayString={(s: IdentifiableNamedObject) => s.name}
                    onChange={(
                        event: React.ChangeEvent<{ value: unknown }>,
                    ) => {
                        setSelectedServiceId(event.target.value as number)
                        setSelectedVendorId(0) // Reset vendor selection when service is selected
                    }}
                    customStyle={{ formControl: { flex: 1, minWidth: 200 } }}
                />
                <Selector
                    label="Vendor Filter"
                    currentValue={selectedVendorId}
                    data={vendorList}
                    getDisplayString={(s: IdentifiableNamedObject) => s.name}
                    onChange={(
                        event: React.ChangeEvent<{ value: unknown }>,
                    ) => {
                        setSelectedVendorId(event.target.value as number)
                        setSelectedServiceId(0) // Reset service selection when vendor is selected
                    }}
                    customStyle={{ formControl: { flex: 1, minWidth: 200 } }}
                />
            </Container>

            <Plot
                data={[
                    {
                        line: { color: 'blue', width: 4 },
                        mode: 'lines',
                        name: 'Forecast',
                        x: forecastedX,
                        y: forecastedY,
                        hovertemplate: '%{x}<br>$%{y:,}<extra></extra>',
                    },
                    {
                        line: { color: 'red', width: 4, dash: 'dash' },
                        mode: 'lines',
                        name: 'Budget',
                        x: forecastedX,
                        y: Array(forecastedX.length).fill(selectedBudget),
                        hovertemplate: '%{x}<br>$%{y:,}<extra></extra>',
                    },
                    {
                        line: { color: '#e3c710', width: 4 },
                        mode: 'lines',
                        name: 'Invoiced',
                        x: invoicedX,
                        y: invoicedY,
                        hovertemplate: '%{x}<br>$%{y:,}<extra></extra>',
                    },
                    {
                        line: { color: 'green', width: 4 },
                        mode: 'lines',
                        name: 'Prediction',
                        x: predictionX,
                        y: predictionY,
                        hovertemplate: '%{x}<br>$%{y:,}<extra></extra>',
                    },
                ]}
                layout={{
                    yaxis: {
                        tickfont: { size: 14 },
                        range: [
                            0,
                            Math.max(
                                ...forecastedY,
                                selectedBudget,
                                ...invoicedY,
                                ...predictionY,
                            ) * 1.1,
                        ],
                    },
                    xaxis: {
                        tickfont: { size: 14 },
                        automargin: true,
                        nticks: numXTicks,
                        tickangle: 30,
                    },
                    legend: {
                        orientation: 'h',
                        yanchor: 'top',
                        y: 1.1,
                        xanchor: 'center',
                        x: 0.5,
                        font: {
                            family: typographyStyle(theme.typography.h1)
                                .fontFamily,
                            size: 16,
                            color: typographyStyle(theme.typography.h1).color,
                        },
                    },
                    hoverlabel: {
                        font: {
                            family: typographyStyle(theme.typography.h1)
                                .fontFamily,
                            size: 18,
                            color: theme.palette.grey[200],
                        },
                    },
                }}
            />
        </Container>
    )
}
